import { useMemo, type ReactNode } from "react";
import type { components } from "../../api/schema";
import { reviewCalls } from "../../lib/markerReview";
import type { ListQueryResult } from "../../lib/useListQuery";
import {
  Button,
  EmptyState,
  Inline,
  InlineError,
  Page,
  PageHeader,
  Skeleton,
  SplitPane,
  Tabs,
  TabPanel,
  Text,
} from "../../ui";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import type { ReviewMarkerSearch } from "./reviewMarkerData";

type Answer = components["schemas"]["AnswerResponse"];
export interface ReviewMarkerViewProps {
  markerName?: string;
  markerPending: boolean;
  error?: string;
  scopeLabel: string;
  canStart: boolean;
  onStart: () => void;
  onBack: () => void;
  tab: ReviewMarkerSearch["tab"];
  onTab: (tab: string) => void;
  needsReviewCount?: number;
  reviewedCount?: number;
  calls: ListQueryResult<Answer>;
  onOpen: (answer: Answer) => void;
  scorecard: ReactNode;
}
export function ReviewMarkerView({
  markerName,
  markerPending,
  error,
  scopeLabel,
  canStart,
  onStart,
  onBack,
  tab,
  onTab,
  needsReviewCount,
  reviewedCount,
  calls,
  onOpen,
  scorecard,
}: ReviewMarkerViewProps) {
  const items = useMemo(() => reviewCalls(calls.items), [calls.items]);
  const count =
    tab === "needs_review"
      ? needsReviewCount
      : tab === "reviewed"
        ? reviewedCount
        : undefined;
  return (
    <Page variant="workspace">
      <PageHeader
        compact
        leading={
          <Inline gap={4}>
            <Button size="sm" variant="secondary" onClick={onBack}>
              Back to Review
            </Button>
            {markerName ? (
              <Text>{markerName}</Text>
            ) : markerPending ? (
              <Skeleton shape="detail-field" label="Loading marker" rows={1} />
            ) : null}
            <Text variant="muted">{scopeLabel}</Text>
          </Inline>
        }
        actions={
          <Button size="sm" disabled={!canStart} onClick={onStart}>
            Start reviewing
          </Button>
        }
      />
      {error ? <InlineError>{error}</InlineError> : null}
      <SplitPane
        firstWidth="half"
        ariaLabel="Marker calls and scorecard"
        first={
          <Page variant="workspace">
            <Tabs
              ariaLabel="Call buckets"
              activeId={tab}
              tabs={[
                {
                  id: "needs_review",
                  label: "Needs review",
                  count: needsReviewCount,
                },
                { id: "reviewed", label: "Reviewed", count: reviewedCount },
                { id: "all", label: "All calls" },
              ]}
              onSelect={onTab}
            />
            <TabPanel tabId={tab} activeId={tab} fill>
              <Text variant="muted">
                {count === undefined
                  ? `${String(items.length)} calls${calls.hasNextPage ? " loaded" : ""}`
                  : `${String(items.length)} of ${String(count)} calls`}
              </Text>
              <PaginatedTableSection
                ariaLabel="Marker calls"
                columns={[
                  { header: "Call", accessorKey: "transcript_label" },
                  {
                    header: "Answer",
                    cell: ({ row }) =>
                      row.original.source === "human" ? "Label" : "Mark",
                  },
                  {
                    header: "Created",
                    cell: ({ row }) =>
                      new Date(row.original.created_at).toLocaleString(),
                  },
                ]}
                items={items}
                getRowId={(answer) => answer.transcript_id}
                isPending={calls.isPending}
                errorMessage={calls.errorMessage}
                hasNextPage={calls.hasNextPage}
                isFetchingNextPage={calls.isFetchingNextPage}
                onLoadMore={calls.fetchNextPage}
                listStateKey={calls.listStateKey}
                onRowClick={(row) => {
                  onOpen(row.original);
                }}
                emptyState={<EmptyState title="No calls in this bucket" />}
                skeletonLabel="Loading calls"
                virtual={{ fill: true }}
              />
            </TabPanel>
          </Page>
        }
        second={scorecard}
      />
    </Page>
  );
}
